import { transformIfAMPCookieFactory, composeCookie } from './cookiesHelpers';
import { isDefined } from '../../utils/types';
/** @import {CookiesConfig} from './types.js' */

/**
 * @param {{
 *  documentAlias: Document,
 *  navigatorAlias: Navigator,
 *  decodeWrapper: function(string): string,
 *  encodeWrapper: function(string | number | boolean): string,
 *  getCookiesConfig: function(): CookiesConfig
 * }} dependencies
 */
export function CookiesService(dependencies) {
  return {
    /**
     * @param {string} id
     * @returns {string}
     */
    transformIfAMPCookie: transformIfAMPCookieFactory(),

    /**
     * Get cookie name with prefix and domain hash
     *
     * @param {string} baseName
     * @returns {string}
     */
    getCookieName(baseName) {
      const { cookieNamePrefix, trackerSiteId, domainHash } = dependencies.getCookiesConfig();
      return cookieNamePrefix + baseName + '.' + trackerSiteId + '.' + domainHash;
    },
    /**
     * Returns cookie value or `0` if cookie not found
     *
     * @param {string} cookieName
     * @returns {string | 0}
     */
    getCookie(cookieName) {
      if (dependencies.getCookiesConfig().cookiesDisabled) {
        return 0;
      }

      const cookiePattern = new RegExp('(^|;)[ ]*' + cookieName + '=([^;]*)');
      const cookieMatch = cookiePattern.exec(dependencies.documentAlias.cookie);
      return cookieMatch ? dependencies.decodeWrapper(cookieMatch[2]) || 0 : 0;
    },
    /**
     * @param {string} cookieName
     * @param {string} value
     * @param {{ msToExpire?: number; path?: string }} [config]
     * @returns {void}
     */
    setCookie(cookieName, value, config) {
      const mergedConfig = dependencies.getCookiesConfig();
      mergedConfig.msToExpire = config
        ? config.msToExpire || mergedConfig.msToExpire
        : mergedConfig.msToExpire;
      mergedConfig.path = config ? config.path || mergedConfig.path : mergedConfig.path;

      if (mergedConfig.cookiesDisabled) {
        return;
      }

      const newCookie = composeCookie(cookieName, dependencies.encodeWrapper(value), mergedConfig);
      document.cookie = newCookie;
    },
    /**
     * @param {string} cookieName
     * @returns {void}
     */
    deleteCookie(cookieName) {
      this.setCookie(cookieName, '', { msToExpire: -86400 });
    },
    /**
     * Does browser have cookies enabled (for this site)?
     *
     * @returns {'1' | '0'}
     */
    hasCookies() {
      if (dependencies.getCookiesConfig().cookiesDisabled) {
        return '0';
      }

      if (!isDefined(dependencies.navigatorAlias.cookieEnabled)) {
        const testCookieName = this.getCookieName('_pk_testcookie');
        this.setCookie(testCookieName, '1');

        const result = this.getCookie(testCookieName) === '1' ? '1' : '0';
        this.deleteCookie(testCookieName);
        return result;
      }

      return dependencies.navigatorAlias.cookieEnabled ? '1' : '0';
    },
  };
}
